import moment from 'moment';
import useWorkGridCell, {isValidDate} from '../../../../../hooks/useWorkGridCell';
import React, {forwardRef, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '../../../../../components/bootstrap/Button';
import Modal, {ModalBody, ModalFooter, ModalHeader, ModalTitle} from '../../../../../components/bootstrap/Modal';
import showNotification from '../../../../../components/extras/showNotification';
import Icon from '../../../../../components/icon/Icon';
import {useMst} from '../../../../../models';
import * as xlsx from 'xlsx';
import CompanyService from "../../../../../services/CompanyService";
import ValidMultiGrid from "../../../../CommonMenu/Member/components/ValidMultiGrid";

const WorkScheduleExcelModal = forwardRef((props) => {
	const { t } = useTranslation();
	const tr = useCallback((name) => t(`workSchedule.${name}`), [t]);

	const { company } = useMst();
	const [validExcel, setValidExcel] = useState([]);
	const [validOpen, setValidOpen] = useState(false);
	const [files, setFiles] = useState('');
	const [users, setUsers] = useState([]);
	const [allUserList, setAllUserList] = useState([]);
	const { formatCell, validateCell: _validateCell } = useWorkGridCell({
		dateKeyMatcher: (key) => key.includes(t('근무일자')),
		empKey: t('사번'),
		allUserList,
	});
	const validateCell = useCallback((dataKey, raw) => {
		// 템플릿 코드: #...# 형식이면 유효하지 않은 코드
		if (dataKey.includes(t('템플릿 코드'))) return String(raw ?? '').includes('#');
		// 사번: #...# 감싸진 경우 또는 목록에 없는 경우
		if (dataKey.includes(t('사번'))) {
			const val = String(raw ?? '').replace(/^#|#$/g, '').trim();
			if (!val) return false; // 빈값은 별도 처리
			return String(raw ?? '').includes('#') || (allUserList.length > 0 && !allUserList.some(u => u.companyNumber === val));
		}
		return _validateCell(dataKey, raw);
	}, [_validateCell, t, allUserList]);
	const skipVerifyRef = useRef(false);
	const [templates, setTemplates] = useState([]);
	const [isEnableUpload, setIsEnableUpload] = useState(false);
	const lastVerified = useRef(null);

	useEffect(() => {
		(async () => {
			// 전체 인원 조회
			const allUsers = (await CompanyService.getAllUser(company.id))?.data ?? [];
			setAllUserList(allUsers);

			// 전체 템플릿 조회
			const templates = (
				await CompanyService.getUserRestType({
					companyId: company.get.id,
					typeArray: ['WORK_SCHDULE', 'REST_SCHDULE']
				})
			)?.data?.filter(i => i.hiddenAt === null) ?? [];
			setTemplates([...props?.defaultTemplates, ...templates]);
		})();
	}, [company.id]);

	useEffect(() => {
		if (validExcel.length === 0) return;
		if (skipVerifyRef.current) { skipVerifyRef.current = false; return; }

		// 이전과 똑같은 소스이면 리턴시켜 무한루프 방지
		if (JSON.stringify(lastVerified.current) === JSON.stringify(validExcel)) return;
		lastVerified.current = validExcel;

		verificationFile(validExcel).then((extractedData) => {
			skipVerifyRef.current = true;
			setValidExcel(addResultField(extractedData.data));
		});
	}, [validExcel]);

	useEffect(() => {
		if (!props?.isModalOpen) {
			setFiles([]);
			setValidOpen(false);
			setValidExcel([]);
		}
	}, [props?.isModalOpen]);

	// allUserList/templates 로드 후 validExcel이 있으면 재검증 (파일 먼저 읽힌 경우 대비)
	useEffect(() => {
		if (allUserList.length > 0 && Array.isArray(templates) && templates.length > 0 && validExcel.length > 0) {
			lastVerified.current = null;
			verificationFile(validExcel).then((extractedData) => {
				skipVerifyRef.current = true;
				setValidExcel(addResultField(extractedData.data));
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allUserList, templates]);

	useEffect(() => {
		setUsers(props.usersOfExcel);
	}, [props.usersOfExcel]);

	// [2024.02.05] 엑셀 유저 목록을 props.usersOfExcel에서 처리하는 걸로 수정해서, refresh() 미사용으로 해당 부분 주석
	// useEffectOnce(() => {
	// 	refresh();
	// }, []);

	// const refresh = async () => {
	// 	const HolyRange = getRange(moment(selectionRange.startDate), moment(selectionRange.endDate), 'd');
	// 	const response = await GroupService.search({ companyId: company.get.id, isUser: true });
	// 	let newGroups = [];
	// 	let items = [];
	// 	let openGroups = {};
	// 	response?.data
	// 		?.filter((g) => g.workSystem.name.indexOf('스케줄') > -1)
	// 		?.map((group) => {
	// 			const thisUserHoly = [...HolyRange]?.filter((v) => {
	// 				return group.paidRestDay.indexOf(parseInt(v.format('E'))) > -1;
	// 			});
	// 			newGroups.push(Object.assign({}, group, { title: group?.name || '', root: true, parent: null }));
	// 			openGroups[parseInt(group.id)] = true;
	// 			if (group.users) {
	// 				group?.users?.map((user) => {
	// 					newGroups.push({ ...user, isRoot: false, parent: group.id, title: user?.user?.name || '', id: user?.userId });
	// 					thisUserHoly?.map((holy) => {
	// 						const isCheckTime = [...items]?.find(
	// 							(v) =>
	// 								v.group === user?.userId &&
	// 								((moment(v['start_time']) >= moment(moment(holy.startOf('d').format('YYYY-MM-DD HH:mm:ss'))) &&
	// 									moment(v['start_time']) <= moment(moment(holy.startOf('d').format('YYYY-MM-DD HH:mm:ss')))) ||
	// 									(moment(v['end_time']) >= moment(moment(holy.endOf('d').format('YYYY-MM-DD HH:mm:ss'))) &&
	// 										moment(v['end_time']) <= moment(moment(holy.endOf('d').format('YYYY-MM-DD HH:mm:ss')))))
	// 						);
	// 					});
	// 				});
	// 			}
	// 		});
	// 	setUsers(newGroups.slice(1));
	// };

	// function getRange(startDate, endDate, type) {
	// 	let fromDate = moment(startDate);
	// 	let toDate = moment(endDate);
	// 	let diff = toDate.diff(fromDate, type);
	// 	let range = [];
	// 	for (let i = 0; i <= diff; i++) {
	// 		range.push(moment(startDate).add(i, type));
	// 	}
	// 	return range;
	// }

	const verificationFile = (data) => {
		return new Promise((resolve) => {
			let result = true;

			const updatedData = data.map((entry) => {
				const updatedEntry = {...entry};
				Object.keys(updatedEntry).forEach((key) => {
					const originalData = updatedEntry[key];

					// 근무일자 유효성 체크
					if (key.includes(t('근무일자'))) {
						let formattedDate = null;
						if (!isNaN(originalData)) {
							if (Number.isInteger(originalData)) {
								const jsDate = new Date((originalData - 25569) * 86400 * 1000);
								formattedDate = moment(jsDate).format('YYYY-MM-DD');
							}
						} else {
							const dateFormats = ['MM/DD/YY', 'MM/DD/YYYY', 'YY/MM/DD', 'YYYY/MM/DD', 'M/D/YY', 'YYYY-MM-DD'];
							for (let i = 0; i < dateFormats.length; i++) {
								const parsedDate = moment(originalData, dateFormats[i], true);
								if (parsedDate.isValid()) {
									formattedDate = parsedDate.format('YYYY-MM-DD');
									break;
								}
							}
						}
						updatedEntry[key] = String(originalData).includes('#')
							? String(originalData)
							: (formattedDate ? formattedDate : `#${originalData}#`);
						result = formattedDate ? result : false;
					}

					// 사번 유효성 체크 (allUserList 로드 전에는 건너뜀)
					if (key.includes(t('사번')) && allUserList?.length > 0) {
						const empStr = String(originalData ?? '').replace(/#/g, '').replace(/ /g, '');
						const matchedUser = allUserList.find(
							user => user.companyNumber === empStr
						);
						if (!matchedUser) {
							const s = String(originalData ?? '');
							updatedEntry[key] = s.includes('#') ? s : `#${empStr}#`;
							result = false;
						} else {
							updatedEntry[t('이름')] = matchedUser.user.name;
						}
					}

					// 템플릿 코드 유효성 체크
					if (key.includes(t('템플릿 코드'))) {
						const codeStr = String(originalData ?? '').replace(/#/g, '').trim();
						// 빈값이면 'F'(dayoff) 코드로 이름 표시, 오류는 아님
						const lookupCode = codeStr || 'F';
						const matchedTemplate = Array.isArray(templates) ? templates.find(tmp => tmp.code === lookupCode) : null;
						if (matchedTemplate) {
							updatedEntry[t('템플릿 이름')] = matchedTemplate.name;
						} else if (codeStr) {
							// 코드 있는데 매칭 안 됨 → 오류
							updatedEntry[key] = `#${codeStr}#`;
							result = false;
						}
					}
				});

				return updatedEntry;
			});

			setIsEnableUpload(result);
			resolve({data: updatedData, result});
		});
	};

	const computeRowResult = (row) => {
		const errors = [];
		// 사번: # 감싸짐 또는 목록에 없음
		Object.entries(row).forEach(([k, v]) => {
			if (!k.includes(t('사번'))) return;
			const empNo = String(v ?? '').replace(/^#|#$/g, '').trim();
			if (!empNo) return;
			const wrapped = String(v).includes('#');
			const notFound = allUserList.length > 0 && !allUserList.some(u => u.companyNumber === empNo);
			if (wrapped || notFound) errors.push(t('존재하지 않는 사번'));
		});
		// 근무일자: 날짜 형식 오류
		if (Object.entries(row).some(([k, v]) => {
			if (!k.includes(t('근무일자'))) return false;
			const val = String(v).replace(/^#|#$/g, '').trim();
			return String(v).includes('#') || !isValidDate(val);
		})) errors.push(t('날짜 형식 오류'));
		// 템플릿 코드: 코드 있는데 매칭 안 됨
		Object.entries(row).forEach(([k, v]) => {
			if (!k.includes(t('템플릿 코드'))) return;
			const codeVal = String(v ?? '').replace(/^#|#$/g, '').trim();
			if (!codeVal) return; // 빈값 = dayoff, 오류 아님
			const notFound = Array.isArray(templates) && templates.length > 0 && !templates.some(tmp => tmp.code === codeVal);
			if (notFound) errors.push(t('유효하지 않은 코드'));
		});
		return { ...row, [t('결과 메시지')]: errors.length > 0 ? errors.join('||') : '정상' };
	};

	const addResultField = (rows) => rows.map(computeRowResult);

	const setValidExcelWithResult = (rows, changedIdx) => {
		skipVerifyRef.current = true;
		if (changedIdx !== undefined) {
			setValidExcel(prev => {
				const next = [...prev];
				next[changedIdx] = computeRowResult(rows[changedIdx]);
				return next;
			});
		} else {
			setValidExcel(addResultField(rows));
		}
	};

	const getCurrentWeekRange = () => {
		const today = new Date();
		const dayOfWeek = today.getDay();
		const diffToMonday = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
		const diffToSunday = diffToMonday + 6;
		const startDate = new Date(today.setDate(diffToMonday));
		const endDate = new Date(today.setDate(diffToSunday));
		return { startDate, endDate };
	};

	const excelFormDownload = () => {
        const xlsx = require('sheetjs-style-v2');
        const workSheetDatas = [];
        const dyDay = [];

		// props.selectionRange에서 startDate와 endDate를 가져옵니다.
		let startDate, endDate;
		if (props.selectionRange?.startDate && props.selectionRange?.endDate) {
			startDate = new Date(props.selectionRange.startDate);
			endDate = new Date(props.selectionRange.endDate);
		} else {
			// If props.selectionRange is not provided, use the current week
			const weekRange = getCurrentWeekRange();
			startDate = weekRange.startDate;
			endDate = weekRange.endDate;
		}

		// 시작일부터 종료일까지 반복합니다.
		let currentDate = new Date(startDate);
		while (currentDate <= endDate) {
			// 월과 일을 두 자리 숫자로 맞춥니다 (예: 8 -> 08).
			const monthText = (currentDate.getMonth() + 1).toString().padStart(2, '0');
			const dayText = currentDate.getDate().toString().padStart(2, '0');
      // YYYY-MM-DD 형식으로 날짜를 만듭니다.
			dyDay.push(`${currentDate.getFullYear()}-${monthText}-${dayText}`);
      // 다음 날짜로 넘어갑니다.
			currentDate.setDate(currentDate.getDate() + 1);
		}

		users.forEach((employee) => {
            dyDay.forEach((day) => {
                workSheetDatas.push({
                    [`${t('이름')}[${t('선택')}]`]: employee.title,
                    [`${t('사번')}[${t('필수')}]`]: employee.companyNumber,
                    [`${t('근무일자')}[${t('필수')}]`]: day,
                    [`${t('템플릿 코드')}[${t('필수')}]`]: ''
                });
            });
		});

        const wb = xlsx.utils.book_new();
        const ws = xlsx.utils.json_to_sheet(workSheetDatas);

        const rangeRef = ws['!ref'];
        const range = xlsx.utils.decode_range(rangeRef);

        const columsW = [];
        const wsrows = [];
        for (let R = range.s.r; R <= range.e.r; ++R) {
            wsrows.push({ hpt: R === 0 ? 45 : 18 });
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cell_ref = xlsx.utils.encode_cell({ c: C, r: R });
                const cell = ws[cell_ref];

                if (!columsW[C]) columsW[C] = 0;
                if (!cell) continue;

                if (typeof cell.v === 'string' && columsW[C] < cell.v.length * 1.5)
                    columsW[C] = cell.v.length * 1.5;

                if (R === 0) {
                    const cellValue = cell?.v?.toString()?.trim() || '';

                    // [선택] 또는 [필수] 포함 시 줄바꿈 적용
                    if (cellValue.includes(`[${t('선택')}]`) || cellValue.includes(`[${t('필수')}]`)) {
                        ws[cell_ref].v = cellValue.replace('[', '\n[');
                        ws[cell_ref].t = 's';
                    }
                    ws[cell_ref].s = {
                        font: { name: '맑은 고딕', bold: true, sz: 11, color: { rgb: "FFFFFFFF" } },
                        fill: {
                            patternType: "solid",
                            // [필수] 포함 시 빨간색으로 표기
                            bgColor: { rgb: cellValue.includes(t('필수')) ? "FFFF5555" : "FF556EE6" },
                            fgColor: { rgb: cellValue.includes(t('필수')) ? "FFFF5555" : "FF556EE6" }
                        },
                        alignment: { vertical: "center", horizontal: "center", wrapText: true },
                        border: { bottom: { style: 'thin', color: { rgb: "FF000000" } } }
                    };
                } else {
                    ws[cell_ref].s = {
                        font: { name: '맑은 고딕', bold: false, sz: 10 },
                        alignment: { vertical: "center", horizontal: "center" }
                    };
                }
            }
        }

        ws['!cols'] = columsW.map(v => ({ wch: parseInt(v) * 1.2 }));
        ws['!rows'] = wsrows;
        xlsx.utils.book_append_sheet(wb, ws, t('근태스케줄'));

		const excelBuffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
		const blob = new Blob([new Uint8Array(excelBuffer)], { type: 'application/octet-stream' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
        a.download = t('adminMode.popup.workplanSampleFile');
		a.click();
		window.URL.revokeObjectURL(url);
	};

	return (
		<Modal size='xl' isStaticBackdrop={true} isOpen={props?.isModalOpen} setIsOpen={props?.setIsModalOpen} isScrollable isCentered>
			<ModalHeader setIsOpen={props?.setIsModalOpen}>
				<ModalTitle style={{ fontSize: '1.4rem', fontWeight: 700 }}>{t('adminMode.popup.uploadWorkPlan')}</ModalTitle>
			</ModalHeader>
			<ModalBody className='h-100' style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 0 }}>

				{/* ── 예시파일 + 업로드 유의사항 (좌우 배치) ── */}
				<div style={{ display: 'flex', gap: 20, marginBottom: 18 }}>
					{/* 예시파일 카드 */}
					<div style={{
						flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 14,
						background: '#fff', border: '1px solid #e2e8f0',
						borderRadius: 14, padding: '12px 18px',
						boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
					}}>
						<svg width="46" height="54" viewBox="0 0 46 54" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
							<rect width="46" height="54" rx="6" fill="#eef2ff" stroke="#c7d7f7" strokeWidth="1"/>
							<rect x="0" y="33" width="46" height="21" rx="6" fill="#4285f4"/>
							<rect x="0" y="29" width="46" height="8" fill="#4285f4"/>
							<text x="23" y="46" textAnchor="middle" fill="white" fontSize="9" fontWeight="800" fontFamily="Arial, sans-serif">XLSX</text>
							<line x1="8" y1="12" x2="38" y2="12" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
							<line x1="8" y1="18" x2="33" y2="18" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
							<line x1="8" y1="24" x2="36" y2="24" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round"/>
						</svg>
						<div style={{ marginRight: 8 }}>
							<div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>{t('예시파일')}</div>
							<div style={{ fontSize: '1rem', color: '#94a3b8', marginTop: 2 }}>{t('xlsx 형식의 업로드 양식을 다운로드하세요')}</div>
						</div>
						<button
							type='button'
							onClick={excelFormDownload}
							style={{
								display: 'flex', alignItems: 'center', gap: 7,
								background: '#556ee6', color: '#fff',
								border: 'none', borderRadius: 10,
								padding: '9px 18px', fontWeight: 700, fontSize: '0.9rem',
								cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
								boxShadow: '0 2px 8px rgba(85,110,230,0.25)',
							}}>
							<Icon icon='Download' color='light' />
							{`xlsx ${t('다운로드')}`}
						</button>
					</div>

					{/* 업로드 유의사항 */}
					<div style={{
						flex: 1, background: '#fffaf2',
						borderRadius: 14, padding: '12px 18px',
						border: '1px solid #fce8c3',
					}}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
							<span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
							<span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#92400e' }}>
								{t('adminMode.popup.uploadNotes')}
							</span>
						</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
							{[
								t('adminMode.popup.onlyXlsxFilesAllowed'),
								t('adminMode.popup.uploadAsIsNote'),
								t('adminMode.popup.requiredFieldsNote'),
								t('workingLog.fixErrorsInHighlightedFieldsBeforeSubmit'),
							].map((text, i) => (
								<div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
									<span style={{
										flexShrink: 0, width: 6, height: 6, borderRadius: '50%',
										background: '#f59e0b', display: 'inline-block',
									}} />
									<span style={{ fontSize: '1rem', color: '#78350f', lineHeight: 1.5 }}>{text}</span>
								</div>
							))}
						</div>
					</div>
				</div>

				{/* ── 파일 업로드 존 ── */}
				<div style={{ marginBottom: 16 }}>
					<input
						id='formFileSm'
						type='file'
						accept='.xlsx,.xls'
						style={{ display: 'none' }}
						onChange={(e) => {
							setFiles(Array.from(e.target.files));
							const fileList = e.target.files;
							for (let i = 0; i < fileList.length; i++) {
								const f = fileList[i];
								const reader = new FileReader();
								reader.onload = function (ev) {
									const data = ev.target.result;
									const workbook = xlsx.read(data, { type: 'binary' });
									const sheetCellData = workbook.Sheets[workbook.SheetNames[0]];
									const range = xlsx.utils.decode_range(sheetCellData['!ref']);
									const dynamicRange = `${xlsx.utils.encode_col(0)}1:${xlsx.utils.encode_col(range.e.c)}${range.e.r + 1}`;
									const sheetData = xlsx.utils.sheet_to_json(sheetCellData, {
										range: dynamicRange, header: 0, defval: '',
									});

									const excelKeys = [t('사번'), t('근무일자'), t('템플릿 코드')];
									const columnOrder = [t('이름'), t('사번'), t('근무일자'), t('템플릿 이름'), t('템플릿 코드')];
									const cleanedSheet = sheetData.map(item => {
										const temp = {};
										for (const key in item) {
											if (!Object.prototype.hasOwnProperty.call(item, key)) continue;
											const cleanedKey = key.replace(/\[.*?\]/g, '').trim();
											if (excelKeys.some(text => key?.includes(text)) || key?.includes(t('이름'))) {
												temp[cleanedKey] = item[key];
											}
										}
										const orderedItem = {};
										columnOrder.forEach(col => { orderedItem[col] = temp[col] ?? ''; });
										return orderedItem;
									});

									let error = null;
									if (!excelKeys.every(k => Object.keys(cleanedSheet[0]).join().includes(k))) {
										error = t('엑셀의 형식이 맞지 않아 파일을 불러올 수 없습니다.');
									} else if (data.length <= 0) {
										error = t('파일을 등록해 주시기 바랍니다.');
									}

									if (!error) {
										verificationFile(cleanedSheet).then((extractedData) => {
											setValidExcel(addResultField(extractedData.data));
											setValidOpen(true);
										});
									} else {
										showNotification(t('adminMode.popup.workPlan'), error, 'danger');
										setValidOpen(false);
										setValidExcel([]);
									}
								};
								reader.readAsBinaryString(f);
							}
						}}
					/>
					<div
						onClick={() => document.getElementById('formFileSm').click()}
						onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#0ab39c'; e.currentTarget.style.background = '#f0fdfb'; }}
						onDragLeave={(e) => { e.currentTarget.style.borderColor = '#94b8e8'; e.currentTarget.style.background = '#fafafa'; }}
						onDrop={(e) => {
							e.preventDefault();
							e.currentTarget.style.borderColor = '#94b8e8';
							e.currentTarget.style.background = '#fafafa';
							const dropped = e.dataTransfer.files;
							if (dropped.length > 0) {
								const input = document.getElementById('formFileSm');
								const dt = new DataTransfer();
								Array.from(dropped).forEach(f => dt.items.add(f));
								input.files = dt.files;
								input.dispatchEvent(new Event('change', { bubbles: true }));
							}
						}}
						style={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							border: '1.7px dashed #8ab4d8', borderRadius: 14,
							padding: '13px 20px', cursor: 'pointer',
							background: '#fafafa', transition: 'all 0.18s',
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
							<div style={{
								width: 44, height: 44, borderRadius: 11, flexShrink: 0,
								background: 'linear-gradient(135deg, #4f8ef7 0%, #7b5ea7 100%)',
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								boxShadow: '0 2px 8px rgba(79,142,247,0.3)',
							}}>
								<Icon icon='CloudUpload' style={{ color: '#fff', fontSize: '1.4rem' }} />
							</div>
							<div>
								<div style={{ fontSize: '0.97rem', fontWeight: 700, color: files.length > 0 ? '#1e293b' : '#64748b' }}>
									{files.length > 0 ? files[0].name : t('파일을 드래그하거나 선택하여 업로드하세요')}
								</div>
								<div style={{ fontSize: '0.92rem', color: '#94a3b8', marginTop: 2 }}>{t('xls, xlsx 파일만 가능')}</div>
							</div>
						</div>
						<button
							type='button'
							onClick={(e) => { e.stopPropagation(); document.getElementById('formFileSm').click(); }}
							style={{
								display: 'flex', alignItems: 'center', gap: 7,
								background: files.length > 0
									? '#cbd5e1'
									: 'linear-gradient(135deg, #0ab39c 0%, #099885 100%)',
								color: files.length > 0 ? '#94a3b8' : '#fff',
								border: 'none', borderRadius: 10,
								padding: '9px 20px', fontWeight: 700, fontSize: '0.92rem',
								cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
								boxShadow: files.length > 0 ? 'none' : '0 2px 10px rgba(10,179,156,0.3)',
							}}>
							<Icon icon='Search' style={{ color: files.length > 0 ? '#94a3b8' : '#fff' }} />
							{t('파일 검사')}
						</button>
					</div>
				</div>

				{/* ── 파일 검사 결과 ── */}
				{validOpen && validExcel.length > 0 && (
					<>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<div style={{
									width: 4, height: 22, borderRadius: 2,
									background: 'linear-gradient(180deg, #556ee6 0%, #8b9cf7 100%)',
									flexShrink: 0,
								}} />
								<span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', whiteSpace: 'nowrap', letterSpacing: '-0.02em' }}>
									{t('파일 검사 결과')}
								</span>
								<span
									onClick={() => { setValidOpen(false); setValidExcel([]); setFiles([]); }}
									style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', background: 'rgba(85,110,230,0.1)', borderRadius: 8, padding: '4px 6px' }}
									title='초기화'
								>
									<Icon icon='Refresh' style={{ fontSize: '1.4rem', color: '#556ee6' }} />
								</span>
							</div>
							<div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem' }}>
								<span style={{ color: '#64748b' }}>
									{t('전체')} <strong style={{ color: '#334155' }}>{validExcel.length}{t('건')}</strong>
								</span>
								{(() => {
									const errCount = validExcel.filter(row => Object.values(row).some(v => String(v).includes('#'))).length;
									return errCount > 0 ? (
										<>
											<span style={{ color: '#cbd5e1' }}>|</span>
											<span style={{ color: '#ef4444', fontWeight: 700 }}>
												{t('오류')} <strong>{errCount}{t('건')}</strong>
											</span>
										</>
									) : null;
								})()}
							</div>
						</div>
						<div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', minHeight: 350 }}>
							<ValidMultiGrid
								dataList={validExcel}
								setData={setValidExcelWithResult}
								allUserList={allUserList}
								templates={templates}
								validateCell={validateCell}
								formatCell={formatCell}
								onDeleteRow={(idx) => {
									const updated = validExcel.filter((_, i) => i !== idx);
									setValidExcel(updated);
									if (updated.length === 0) { setValidOpen(false); setFiles([]); }
								}}
							/>
						</div>
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<Button
					type={'button'}
					icon='FileUpload'
					color={'success'}
					onClick={async () => {
						const hasError = validExcel.some(row => {
							const msg = String(row[t('결과 메시지')] ?? '');
							return msg !== '정상' && msg !== '';
						});
						if (!isEnableUpload || hasError) {
							showNotification(t('adminMode.mainpage.scheduleCreation'), tr('uploadAfterFix'), 'danger');
						} else {
							props?.setExcelItems(validExcel);
							props?.setIsModalOpen(false);
						}
					}}>
					{t('업로드')}
				</Button>
			</ModalFooter>
		</Modal>
	);
});

export default WorkScheduleExcelModal;
